import React, { useState } from 'react';
import MenuItemPicker from '../notInMenu/MenuItemPicker';
import '../notInMenu/NotInMenu.css';
import {
  SKIP_REASONS, buildSkipReason, effectiveAction, formatNumber, money, priceText, sameName, skipReasonLabel,
} from './sncShared';

/**
 * One POS name in the worklist (spec §12 "Rows"): name and spellings, plates,
 * the suggestion with its tags, the "not suggested" note, Link / Add / Skip,
 * the inline controls, the unsaved state and the Apply result badge.
 * Every change goes through actions.* in the container, which owns saving.
 */

const RESULT_BADGES = {
  saved: { cls: 'snc-badge--saved', text: 'Saved' },
  price_decides: { cls: 'snc-badge--price', text: 'Matched by price for each sale' },
  conflict: { cls: 'snc-badge--conflict', text: 'Conflict' },
  left_for_nim: { cls: 'snc-badge--nim', text: 'Left for Not in Menu' },
};

const stop = (e) => e.stopPropagation();

const dishName = (menuItemId, row, menu) => {
  const id = Number(menuItemId);
  const fromProposal = ((row.flags && row.flags.sizeSplitProposal) || []).find((p) => Number(p.menuItemId) === id);
  if (fromProposal && fromProposal.name) return fromProposal.name;
  const fromSuggestion = (row.suggestions || []).find((s) => Number(s.menuItemId) === id);
  if (fromSuggestion && fromSuggestion.name) return fromSuggestion.name;
  const fromMenu = (menu || []).find((m) => Number(m.menu_item_id ?? m.id) === id);
  return fromMenu ? fromMenu.name : `Dish #${id}`;
};

const splitText = (entries, row, menu) => entries
  .map((p) => `${priceText(p.price)} → ${p.name || dishName(p.menuItemId, row, menu)}`)
  .join(' · ');

function Tags({ tags }) {
  return (tags || []).map((tag) => <span key={tag} className="snc-tag">{tag}</span>);
}

function SuggestionCell({ row, menu }) {
  const d = row.decision;
  if (d && d.action === 'link' && Array.isArray(d.priceSplit) && d.priceSplit.length > 0) {
    return (
      <div className="snc-suggestion">
        <span className="snc-suggestion__name">Price decides: {splitText(d.priceSplit, row, menu)}</span>
      </div>
    );
  }
  if (d && d.action === 'link') {
    return (
      <div className="snc-suggestion">
        <span className="snc-suggestion__name">{d.targetName || dishName(d.targetMenuItemId, row, menu)}</span>
        <span className="snc-tag">linked</span>
      </div>
    );
  }
  if (d && d.action === 'add') {
    return (
      <div className="snc-suggestion">
        <span className="snc-suggestion__name">New dish: {d.addName || row.displayName}</span>
        <span className="snc-tag">{priceText(d.addPrice)}</span>
      </div>
    );
  }
  if (d && d.action === 'skip') {
    return (
      <div className="snc-suggestion">
        <span className="snc-suggestion__none">Skipped</span>
        {d.skipReason && <span className="snc-tag">{skipReasonLabel(d.skipReason)}</span>}
      </div>
    );
  }
  if (row.group === 'matched') {
    if (row.flags && row.flags.rememberedSkip) {
      return (
        <div className="snc-suggestion">
          <span className="snc-suggestion__none">Not a dish</span>
          <span className="snc-tag">remembered</span>
        </div>
      );
    }
    return (
      <div className="snc-suggestion">
        <span className="snc-suggestion__name">{row.matchedMenuItem ? row.matchedMenuItem.name : 'Matched by price'}</span>
        <span className="snc-tag">{row.matchSource === 'remembered' ? 'remembered' : 'exact name'}</span>
      </div>
    );
  }
  const top = (row.suggestions || [])[0];
  if (top) {
    return (
      <div className="snc-suggestion">
        <span className="snc-suggestion__name">{top.name}</span>
        <Tags tags={top.tags} />
      </div>
    );
  }
  return (
    <div className="snc-suggestion">
      <span className="snc-suggestion__none">No match</span>
      {row.flags && row.flags.tooGeneric && <span className="snc-tag">too generic</span>}
    </div>
  );
}

function AddPanel({ row, busy, loadAddCheck, onSaveAdd, onLinkInstead, onCancel }) {
  const d = row.decision || {};
  const [name, setName] = useState(d.addName || row.displayName || '');
  const [priceValue, setPriceValue] = useState(
    d.addPrice != null ? String(d.addPrice) : (row.mainPrice != null ? String(row.mainPrice) : ''),
  );
  const [checking, setChecking] = useState(false);
  const [didYouMean, setDidYouMean] = useState(null);
  const [error, setError] = useState('');

  const priceNum = Number(priceValue);
  const valid = name.trim().length > 0 && priceValue !== '' && Number.isFinite(priceNum) && priceNum >= 0;

  // Spec §8.2: look for an existing dish before an Add decision is saved.
  const check = async () => {
    if (!valid || checking || busy) return;
    setChecking(true);
    setError('');
    const r = await loadAddCheck(row);
    setChecking(false);
    if (!r.ok) {
      setError(`${r.error} You can still add it.`);
      setDidYouMean([]);
      return;
    }
    const list = Array.isArray(r.didYouMean) ? r.didYouMean : [];
    if (list.length === 0) {
      onSaveAdd(row, name.trim(), priceNum);
      return;
    }
    setDidYouMean(list);
  };

  return (
    <div className="snc-panel" onClick={stop}>
      <div className="snc-panel__head">
        <span>Add “{row.displayName}” as a new dish</span>
        <button type="button" className="snc-link-btn" onClick={onCancel}>Cancel</button>
      </div>
      <div className="snc-panel__row">
        <label className="snc-field">
          <span>Dish name</span>
          <input
            className="snc-input"
            value={name}
            disabled={busy || checking}
            onChange={(e) => { setName(e.target.value); setDidYouMean(null); }}
          />
        </label>
        <label className="snc-field snc-field--price">
          <span>Price (₹)</span>
          <input
            className="snc-input"
            type="number"
            min="0"
            step="0.01"
            value={priceValue}
            disabled={busy || checking}
            onChange={(e) => { setPriceValue(e.target.value); setDidYouMean(null); }}
          />
        </label>
      </div>
      {didYouMean === null && (
        <div className="snc-panel__actions">
          <button type="button" className="snc-btn snc-btn--primary snc-btn--small" disabled={!valid || busy || checking} onClick={check}>
            {checking ? 'Checking…' : 'Add dish'}
          </button>
        </div>
      )}
      {didYouMean !== null && (
        <div className="snc-dym">
          {didYouMean.map((c) => (
            <div key={c.menuItemId} className="snc-dym__row">
              <span>
                Did you mean <strong>{c.name}</strong>
                {(c.unclaimed || (c.tags || []).includes('0 sales this month')) ? ' (0 sales this month)' : ''}
                {c.price != null ? ` · ${priceText(c.price)}` : ''}?
              </span>
              <button type="button" className="snc-action" disabled={busy} onClick={() => onLinkInstead(row, c.menuItemId)}>
                Link to it
              </button>
            </div>
          ))}
          {error && <p className="snc-error">{error}</p>}
          <div className="snc-panel__actions">
            <button
              type="button"
              className="snc-btn snc-btn--outline snc-btn--small"
              disabled={!valid || busy}
              onClick={() => onSaveAdd(row, name.trim(), priceNum)}
            >
              {didYouMean.length > 0 ? `No — add “${name.trim()}” as a new dish` : `Add “${name.trim()}”`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function SkipPanel({ row, busy, onSaveSkip, onCancel }) {
  const [reason, setReason] = useState('');
  const [text, setText] = useState('');
  const skipReason = buildSkipReason(reason, text);
  return (
    <div className="snc-panel" onClick={stop}>
      <div className="snc-panel__head">
        <span>Why skip “{row.displayName}”?</span>
        <button type="button" className="snc-link-btn" onClick={onCancel}>Cancel</button>
      </div>
      <p className="snc-note">
        It sold {money(row.amount)} or looks like a dish, so say why it is not a stock item. Its sales still count in your totals.
      </p>
      <div className="snc-panel__row">
        <select className="snc-select" value={reason} disabled={busy} onChange={(e) => setReason(e.target.value)} aria-label="Skip reason">
          <option value="">Choose a reason…</option>
          {SKIP_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
        </select>
        {reason === 'other' && (
          <input
            className="snc-input"
            placeholder="What is it?"
            value={text}
            disabled={busy}
            onChange={(e) => setText(e.target.value)}
            aria-label="Other reason"
          />
        )}
        <button
          type="button"
          className="snc-btn snc-btn--primary snc-btn--small"
          disabled={!skipReason || busy}
          onClick={() => onSaveSkip(row, skipReason)}
        >
          Skip
        </button>
      </div>
    </div>
  );
}

export default function NameRow({
  row, ui, focused, selected, panel, menu, preferIds, labelFor, pageBusy, actions,
}) {
  const [showSpellings, setShowSpellings] = useState(false);
  const saving = Boolean(ui && ui.saving);
  const busy = Boolean(pageBusy) || saving;
  const pendingBody = ui && (ui.saving || ui.error) && ui.body ? ui.body : null;
  const shownAction = pendingBody ? pendingBody.action : effectiveAction(row);
  const unsavedAction = ui && ui.error && ui.body ? ui.body.action : null;

  const d = row.decision;
  const flags = row.flags || {};
  const spellings = row.spellings || [];
  const hasSplit = Boolean(d && d.action === 'link' && Array.isArray(d.priceSplit) && d.priceSplit.length > 0);
  const proposal = Array.isArray(flags.sizeSplitProposal) && flags.sizeSplitProposal.length >= 2 ? flags.sizeSplitProposal : null;

  // The dish this name is (or would be) linked to — the inline Link controls act on it.
  let linkTarget = null;
  if (d && d.action === 'link' && !hasSplit && d.targetMenuItemId) {
    linkTarget = { id: d.targetMenuItemId, name: d.targetName || dishName(d.targetMenuItemId, row, menu) };
  } else if (!(d && d.action) && row.group === 'matched' && row.matchedMenuItem) {
    linkTarget = { id: row.matchedMenuItem.menuItemId, name: row.matchedMenuItem.name };
  }
  const priceGap = (row.warnings || []).find((w) => w.code === 'PRICE_GAP');
  const sharedDish = (row.warnings || []).find((w) => w.code === 'SHARED_DISH');
  const showUsePos = Boolean(linkTarget && linkTarget.name && !sameName(linkTarget.name, row.displayName));
  const usePosOn = Boolean(d && d.action === 'link' && d.usePosName);
  const newPriceOn = Boolean(d && d.action === 'link' && d.newPrice != null);

  const chips = (row.suggestions || []).map((s) => ({
    menu_item_id: s.menuItemId,
    name: s.name,
    price_exact: (s.tags || []).includes('same price'),
  }));

  const result = row.result && RESULT_BADGES[row.result.code] ? row.result : null;
  const detail = (result && result.detail) || {};
  const ruledOut = row.ruledOut || [];

  const actionClass = (action) => [
    'snc-action',
    shownAction === action ? 'snc-action--active' : '',
    unsavedAction === action ? 'snc-action--unsaved' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      id={`snc-row-${row.nameId}`}
      className={`snc-row-wrap${focused ? ' snc-row-wrap--focused' : ''}${selected ? ' snc-row-wrap--selected' : ''}`}
      onClick={() => actions.focus(row)}
    >
      <div className={`snc-row${shownAction ? ' snc-row--decided' : ''}`}>
        <div className="snc-row__name">
          <input
            type="checkbox"
            className="snc-row__check"
            checked={selected}
            onClick={stop}
            onChange={() => actions.toggleSelect(row)}
            aria-label={`Select ${row.displayName}`}
          />
          <span>{row.displayName}</span>
          {spellings.length > 1 && (
            <button
              type="button"
              className="snc-link-btn snc-row__spellings-btn"
              onClick={(e) => { e.stopPropagation(); setShowSpellings((v) => !v); }}
              aria-expanded={showSpellings}
            >
              {spellings.length} spellings
            </button>
          )}
          <span className="snc-row__sub">
            {money(row.amount)}{row.mainPrice ? ` · main price ${priceText(row.mainPrice)}` : ''}
          </span>
        </div>
        <span className="snc-num snc-row__plates">{formatNumber(row.plates)}</span>
        <SuggestionCell row={row} menu={menu} />
        <div className="snc-actions">
          <button type="button" className={actionClass('link')} disabled={busy} onClick={(e) => { e.stopPropagation(); actions.link(row); }}>
            Link
          </button>
          <button type="button" className={actionClass('add')} disabled={busy} onClick={(e) => { e.stopPropagation(); actions.add(row); }}>
            Add
          </button>
          <button type="button" className={actionClass('skip')} disabled={busy} onClick={(e) => { e.stopPropagation(); actions.skip(row); }}>
            Skip
          </button>
        </div>
      </div>

      {showSpellings && (
        <div className="snc-spellings">
          {spellings.map((s) => (
            <span key={s.text} className="snc-spellings__item">{s.text} · {formatNumber(s.plates)} plates</span>
          ))}
        </div>
      )}

      {ruledOut.length > 0 && (
        <p className="snc-note">
          Not suggested: {ruledOut.slice(0, 2).map((r) => `${r.name} (${r.reason})`).join(', ')}
          {ruledOut.length > 2 ? ` and ${ruledOut.length - 2} more` : ''}
        </p>
      )}
      {Array.isArray(flags.exactAmbiguous) && flags.exactAmbiguous.length > 1 && (
        <p className="snc-note snc-note--warn">Several dishes have exactly this name. Link the right one.</p>
      )}
      {flags.rememberedInactive && (
        <p className="snc-note snc-note--warn">The dish this name was remembered for is no longer active. Link another dish, or Add it.</p>
      )}
      {flags.tooGeneric && (
        <p className="snc-note">Too general to match to a dish. Skip it, or Add it as a dish.</p>
      )}
      {sharedDish && (
        <p className="snc-note snc-note--warn">
          “{sharedDish.otherName}” is linked to the same dish at {priceText(sharedDish.otherPrice)}: these may be Half and Full.
        </p>
      )}

      {proposal && !hasSplit && (
        <div className="snc-split" onClick={stop}>
          <span>Sold at two sizes: {splitText(proposal, row, menu)}</span>
          <button type="button" className="snc-action" disabled={busy} onClick={() => actions.acceptSplit(row)}>Accept</button>
        </div>
      )}

      {linkTarget && (showUsePos || priceGap) && (
        <div className="snc-panel snc-panel--inline" onClick={stop}>
          {showUsePos && (
            <label className="snc-check">
              <input
                type="checkbox"
                checked={usePosOn}
                disabled={busy}
                onChange={(e) => actions.setLinkOption(row, { usePosName: e.target.checked })}
              />
              Use the POS name “{row.displayName}” for {linkTarget.name}
            </label>
          )}
          {priceGap && (
            <label className="snc-check">
              <input
                type="checkbox"
                checked={newPriceOn}
                disabled={busy}
                onChange={(e) => actions.setLinkOption(row, { newPrice: e.target.checked ? Number(priceGap.posPrice) : null })}
              />
              Update menu price to {priceText(priceGap.posPrice)} (the menu says {priceText(priceGap.menuPrice)})
            </label>
          )}
        </div>
      )}

      {panel === 'link' && (
        <div className="snc-panel" onClick={stop}>
          <div className="snc-panel__head">
            <span>Link “{row.displayName}” to</span>
            <button type="button" className="snc-link-btn" onClick={actions.closePanel}>Cancel</button>
          </div>
          <MenuItemPicker
            menuItems={menu}
            suggestions={chips}
            disabled={busy}
            preferIds={preferIds}
            labelFor={labelFor}
            autoFocus
            onPick={(item) => actions.pickDish(row, item.menu_item_id ?? item.id)}
          />
        </div>
      )}
      {panel === 'add' && (
        <AddPanel
          row={row}
          busy={busy}
          loadAddCheck={actions.loadAddCheck}
          onSaveAdd={actions.saveAdd}
          onLinkInstead={actions.pickDish}
          onCancel={actions.closePanel}
        />
      )}
      {panel === 'skip' && (
        <SkipPanel row={row} busy={busy} onSaveSkip={actions.saveSkip} onCancel={actions.closePanel} />
      )}

      {saving && <div className="snc-rowstate">Saving…</div>}
      {!saving && ui && ui.error && (
        <div className="snc-rowstate snc-rowstate--error" role="alert">
          {ui.error}
          {ui.body && (
            <button type="button" className="snc-link-btn" onClick={(e) => { e.stopPropagation(); actions.retry(row); }}>
              Retry
            </button>
          )}
        </div>
      )}

      {result && (
        <div className="snc-result" onClick={stop}>
          <span className={`snc-badge ${RESULT_BADGES[result.code].cls}`}>{RESULT_BADGES[result.code].text}</span>
          {result.code === 'conflict' && detail.message && <span className="snc-result__detail">{detail.message}</span>}
          {result.code === 'conflict' && detail.existing && (
            <>
              <button type="button" className="snc-action" disabled={busy} onClick={() => actions.replaceConflict(row)}>Replace</button>
              <button type="button" className="snc-action" disabled={busy} onClick={() => actions.keepExisting(row)}>Keep existing</button>
            </>
          )}
          {(result.code === 'saved' || result.code === 'price_decides') && (
            <button type="button" className="snc-link-btn" disabled={busy} onClick={() => actions.undoApply(row)}>Undo</button>
          )}
        </div>
      )}
    </div>
  );
}
